import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CheckBox from '../core/Checkbox';
import { cn } from '@/helpers/classnames';
import { PeriodDateRange } from '@/components/PeriodDateRange';
import { toPeriodDate } from '@/helpers/periodEndDate';
import { periodConfigurationKey } from '@/helpers/periodConfiguration';
import { getPeriodStatus, periodStatusMessages } from '@/helpers/periodStatus';
import { useTenantStaticTicketTitles } from '@/hooks/useStaticTicketTitles';
import { useTenantID } from '@/hooks/useTenantID';
import { FreePeriodBadge } from './FreePeriodBadge';
import { PeriodStatusBadge } from './PeriodStatusBadge';
import { PeriodTicketKind } from './PeriodTicketKind';
import { SelfManagedPeriodChip } from './SelfManagedPeriodChip';
import type {
  CollectionTicketClaimDTO,
  OrganizationDTO,
} from '@/api/trafikportalen';
import IconButton from '../core/IconButton';
import { IconArrowRight } from '@/icons';

interface TenantPeriodOrgRowProps {
  organization: OrganizationDTO;
  claims: CollectionTicketClaimDTO[];
  canEdit: boolean;
  orgSelected: boolean;
  highlighted: boolean;
  selectedPeriodKey: string | null;
  onOrgSelectedChange: (organizationId: string, selected: boolean) => void;
  onPeriodSelect: (claim: CollectionTicketClaimDTO) => void;
}

export const TenantPeriodOrgRow = ({
  organization,
  claims,
  canEdit,
  orgSelected,
  highlighted,
  selectedPeriodKey,
  onOrgSelectedChange,
  onPeriodSelect,
}: TenantPeriodOrgRowProps) => {
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();
  const ticketTitle = useTenantStaticTicketTitles();

  const currentClaims = useMemo(() => {
    const current = claims.filter((claim) => getPeriodStatus(claim) !== 'past');
    current.sort((a, b) =>
      (b.valid_from ?? '').localeCompare(a.valid_from ?? ''),
    );
    return current;
  }, [claims]);

  const periodAccess = organization.selfmanaged_school_claims === true;

  return (
    <li
      aria-label={organization.name}
      className={cn(
        'flex flex-col gap-3 rounded-md border p-4',
        highlighted
          ? 'border-primary-600 bg-primary-50'
          : 'border-background-300 bg-background-50',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          {canEdit && (
            <CheckBox
              id={`tenant-period-org-${organization.id}`}
              checked={orgSelected}
              aria-label={formatMessage(
                { defaultMessage: 'Markera {name}' },
                { name: organization.name },
              )}
              onCheckedChange={(checked) =>
                onOrgSelectedChange(organization.id, checked === true)
              }
            />
          )}
          <span className="flex min-w-0 flex-col">
            <span className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="truncate font-medium">{organization.name}</span>
              {periodAccess && <SelfManagedPeriodChip />}
            </span>
            <span className="text-text-800">
              {organization.organization_number}
            </span>
            {highlighted && (
              <span className="text-sm font-medium">
                <FormattedMessage defaultMessage="Använder vald period" />
              </span>
            )}
          </span>
        </div>
        <IconButton
          icon={<IconArrowRight />}
          variant="ghost"
          aria-label={formatMessage(
            { defaultMessage: 'Visa {name}' },
            { name: organization.name },
          )}
          to="/$tenantId/organizations/organization/$organizationId"
          params={{ tenantId, organizationId: organization.id }}
          title={formatMessage(
            { defaultMessage: 'Visa {name}' },
            { name: organization.name },
          )}
        />
      </div>

      <div
        role="group"
        aria-label={formatMessage(
          { defaultMessage: 'Aktuella perioder för {name}' },
          { name: organization.name },
        )}
      >
        {currentClaims.length === 0 ? (
          <i className="text-text-700">
            <FormattedMessage defaultMessage="Inga aktuella perioder" />
          </i>
        ) : (
          <ul className="mt-1 flex flex-col gap-2">
            {currentClaims.map((claim) => {
              const status = getPeriodStatus(claim);
              const range = `${claim.valid_from ? toPeriodDate(claim.valid_from) : '—'} – ${
                claim.valid_to ? toPeriodDate(claim.valid_to, 'end') : '—'
              }`;
              const selected =
                selectedPeriodKey !== null &&
                periodConfigurationKey(claim) === selectedPeriodKey;
              const staticName = claim.relational
                ? undefined
                : ticketTitle(claim.stina_product_id);
              return (
                <li key={claim.id}>
                  <button
                    type="button"
                    aria-pressed={selected}
                    aria-label={
                      claim.relational
                        ? formatMessage(
                            {
                              defaultMessage:
                                'Perioden {range} för {name}, {status}{free, select, yes {, kostnadsfri} other {}}',
                            },
                            {
                              range,
                              name: organization.name,
                              status: formatMessage(
                                periodStatusMessages[status],
                              ),
                              free: claim.non_invoiced ? 'yes' : 'no',
                            },
                          )
                        : formatMessage(
                            {
                              defaultMessage:
                                'Perioden {range} för {name}, {status}, {ticket}{free, select, yes {, kostnadsfri} other {}}',
                            },
                            {
                              range,
                              name: organization.name,
                              status: formatMessage(
                                periodStatusMessages[status],
                              ),
                              ticket:
                                staticName ??
                                formatMessage({
                                  defaultMessage: 'Statisk biljett',
                                }),
                              free: claim.non_invoiced ? 'yes' : 'no',
                            },
                          )
                    }
                    onClick={() => onPeriodSelect(claim)}
                    className={cn(
                      'flex w-full flex-wrap items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-background-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600',
                      selected &&
                        'bg-primary-100 font-medium ring-2 ring-primary-600',
                    )}
                  >
                    <PeriodDateRange
                      from={claim.valid_from}
                      to={claim.valid_to}
                    />
                    {!claim.relational && (
                      <PeriodTicketKind
                        staticTicketName={staticName}
                        relational={claim.relational ?? false}
                      />
                    )}
                    <PeriodStatusBadge status={status} />
                    {claim.non_invoiced && <FreePeriodBadge />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </li>
  );
};
